import React, { useState } from 'react';
import { auth } from '../../lib/firebase';
import { createListing, getListingById, updateListing, deleteListing } from '../../services/listings';
import { createTicketLink, updateTicketLink, type TicketLink } from '../../services/ticketLinks';
import { uploadListingImage } from '../../services/storage';
import { NIGERIAN_STATES } from '../../data/nigerianStates';
import { NIGERIAN_UNIVERSITIES } from '../../data/nigerianUniversities';
import { toUserMessage } from '../../utils/errors';

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  editLink?: TicketLink | null;
}

interface TierRow { name: string; price: string; quantity: string; perks: string; codePrefix: string }

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ isOpen, onClose, onCreated, editLink }) => {
  const isEdit = !!editLink;
  const [title, setTitle] = useState('');
  const [locationType, setLocationType] = useState<'school' | 'outside' | 'online'>('school');
  const [school, setSchool] = useState('');
  const [stateName, setStateName] = useState('');
  const [onlineUrl, setOnlineUrl] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [venue, setVenue] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [tiers, setTiers] = useState<TierRow[]>([{ name: 'Regular', price: '', quantity: '', perks: '', codePrefix: '' }]);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [creating, setCreating] = useState(false);

  // Prefill when opened in edit mode.
  const seededFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isOpen) { seededFor.current = null; return; }
    if (editLink && seededFor.current !== editLink.id) {
      seededFor.current = editLink.id || 'edit';
      setTitle(editLink.title || '');
      if ((editLink as any).onlineUrl) { setLocationType('online'); setOnlineUrl((editLink as any).onlineUrl || ''); setSchool(''); setStateName(''); }
      else if (editLink.university) { setLocationType('school'); setSchool(editLink.university); setStateName(''); setOnlineUrl(''); }
      else { setLocationType('outside'); setStateName((editLink as any).state || ''); setSchool(''); setOnlineUrl(''); }
      setEventDate(editLink.eventDate || '');
      setVenue(editLink.venue || '');
      setDescription(editLink.description || '');
      setExistingImages(Array.isArray(editLink.images) ? editLink.images : []);
      setImages([]);
      const rows = (editLink.tiers && editLink.tiers.length)
        ? editLink.tiers.map((t) => ({ name: t.name || '', price: t.price != null ? String(t.price) : '', quantity: t.quantity != null ? String(t.quantity) : '', perks: t.perks || '', codePrefix: (t as any).codePrefix || '' }))
        : [{ name: 'Regular', price: editLink.price != null ? String(editLink.price) : '', quantity: '', perks: '', codePrefix: '' }];
      setTiers(rows);
      setVisibility((editLink as any).visibility === 'private' ? 'private' : 'public');
    }
  }, [isOpen, editLink]);

  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [directUrl, setDirectUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const previews = React.useMemo(
    () => images.map((f) => ({ url: URL.createObjectURL(f), isVideo: f.type.startsWith('video/'), name: f.name })),
    [images]
  );
  React.useEffect(() => () => { previews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} }); }, [previews]);

  const addFiles = (list: FileList | null) => {
    if (list && list.length) setImages((prev) => [...prev, ...Array.from(list)]);
  };
  const removeImage = (i: number) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const patchTier = (i: number, next: Partial<TierRow>) =>
    setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...next } : t)));

  const reset = () => {
    setTitle(''); setLocationType('school'); setSchool(''); setStateName(''); setOnlineUrl(''); setEventDate(''); setVenue(''); setDescription('');
    setImages([]); setTiers([{ name: 'Regular', price: '', quantity: '', perks: '', codePrefix: '' }]);
    setVisibility('public');
    setError(null); setShareUrl(null); setDirectUrl(null); setCopied(false);
  };

  const handleCreate = async () => {
    const user = auth.currentUser;
    if (!user) { setError('Please log in.'); return; }
    if (title.trim().length < 2) { setError('Enter an event title.'); return; }
    if (locationType === 'school' && school.trim().length < 2) { setError('Select the school the event is in.'); return; }
    if (locationType === 'outside' && !stateName) { setError('Select the state the event is in.'); return; }
    if (locationType === 'online' && !/^https?:\/\/.+/i.test(onlineUrl.trim())) { setError('Enter a valid event link (starting with http:// or https://).'); return; }

    // Only the first tier is required; the rest are optional.
    const validTiers = tiers
      .filter((t) => t.name.trim() && Number(t.price) > 0)
      .map((t) => ({
        name: t.name.trim(),
        price: Number(t.price),
        quantity: t.quantity.trim() ? Number(t.quantity) : undefined,
        perks: t.perks.trim() || undefined,
        codePrefix: t.codePrefix.trim() || undefined,
      }));
    if (validTiers.length === 0) { setError('Add at least one ticket type with a name and price.'); return; }

    const basePrice = Math.min(...validTiers.map((t) => t.price));
    const totalQty = validTiers.reduce((sum, t) => sum + (t.quantity || 0), 0);
    const maxQuantity = totalQty > 0 ? totalQty : undefined;

    const isSchool = locationType === 'school';
    const isOnline = locationType === 'online';
    const isPrivate = visibility === 'private';
    const cleanOnlineUrl = onlineUrl.trim();
    const placeName = isOnline ? 'Online' : isSchool ? school.trim() : stateName;

    setCreating(true);
    setError(null);
    try {
      // Preserve each tier's sold count when editing (match by name); clean undefined.
      const soldByName: Record<string, number> = {};
      (editLink?.tiers || []).forEach((t) => { soldByName[t.name] = Number(t.soldCount || 0); });
      const cleanTiers = validTiers.map((t) => {
        const tier: any = { name: t.name, price: t.price, soldCount: soldByName[t.name] ?? 0 };
        if (typeof t.quantity === 'number' && !isNaN(t.quantity)) tier.quantity = t.quantity;
        if (t.perks) tier.perks = t.perks;
        if (t.codePrefix) tier.codePrefix = t.codePrefix;
        return tier;
      });

      let linkId = editLink?.id || '';

      if (isEdit && editLink?.id) {
        const newUrls = images.length ? await Promise.all(images.map((f) => uploadListingImage(f, user.uid))) : [];
        const finalImages = [...existingImages, ...newUrls];

        const listingPatch: any = {
          title: title.trim(),
          price: basePrice,
          description: description.trim() || undefined,
          location: placeName,
          university: isSchool ? school.trim() : undefined,
          category: 'tickets',
          type: 'ticket',
          ticketTiers: cleanTiers,
          images: finalImages,
          specifications: { eventDate: eventDate || '', venue: venue.trim() || '', [isSchool ? 'school' : 'state']: placeName },
        };

        // Keep the marketplace listing in sync with the chosen visibility.
        let nextListingId: string | null = editLink.listingId || null;
        if (isPrivate && editLink.listingId) {
          // Going private: remove the public marketplace listing.
          try { await deleteListing(editLink.listingId); } catch { /* may already be gone */ }
          nextListingId = null;
        } else if (!isPrivate && !editLink.listingId) {
          // Going public with no listing yet: create one.
          nextListingId = await createListing({ ...listingPatch } as any, images, user);
        } else if (!isPrivate && editLink.listingId) {
          await updateListing(editLink.listingId, listingPatch);
        }

        await updateTicketLink(editLink.id, {
          title: title.trim(),
          price: basePrice,
          description: description.trim(),
          tiers: cleanTiers,
          eventDate: eventDate || '',
          venue: venue.trim(),
          state: isSchool || isOnline ? '' : stateName,
          university: isSchool ? school.trim() : '',
          onlineUrl: isOnline ? cleanOnlineUrl : '',
          images: finalImages,
          visibility,
          listingId: nextListingId,
          maxQuantity: typeof maxQuantity === 'number' ? maxQuantity : null,
        } as any);
      } else {
        const listingData: any = {
          title: title.trim(),
          price: basePrice,
          category: 'tickets',
          type: 'ticket',
          description: description.trim() || undefined,
          location: placeName,
          university: isSchool ? school.trim() : undefined,
          ticketTiers: cleanTiers,
          specifications: { eventDate: eventDate || '', venue: venue.trim() || '', [isSchool ? 'school' : 'state']: placeName },
        };
        // Public events get a marketplace listing; private events are link-only.
        let listingId: string | undefined;
        let created: any = null;
        if (!isPrivate) {
          listingId = await createListing(listingData, images, user);
          created = await getListingById(listingId).catch(() => null);
        } else if (images.length) {
          // Private: no marketplace listing, but still upload the flyer for the link.
          const urls = await Promise.all(images.map((f) => uploadListingImage(f, user.uid)));
          created = { images: urls };
        }

        const res = await createTicketLink({
          sellerId: user.uid,
          sellerName: user.displayName || undefined,
          sellerEmail: user.email || undefined,
          title: title.trim(),
          price: basePrice,
          description: description.trim() || undefined,
          maxQuantity,
          tiers: validTiers,
          eventDate: eventDate || undefined,
          venue: venue.trim() || undefined,
          state: isSchool || isOnline ? undefined : stateName,
          university: isSchool ? school.trim() : undefined,
          onlineUrl: isOnline ? cleanOnlineUrl : undefined,
          visibility,
          images: created?.images?.length ? created.images : undefined,
          listingId,
        });
        linkId = res.id;
      }

      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      // Share via the OG endpoint so WhatsApp/social previews show the flyer + title.
      setShareUrl(`${origin}/api/ticket-share?id=${linkId}`);
      setDirectUrl(`${origin}/tickets/${linkId}`);
      onCreated?.();
    } catch (e: any) {
      setError(toUserMessage(e) || `Could not ${isEdit ? 'save' : 'create'} the event. Please try again.`);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <i className="ri-ticket-2-line text-purple-600"></i> {isEdit ? 'Edit Event' : 'Create Event Ticket'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><i className="ri-close-line text-2xl"></i></button>
        </div>

        {shareUrl ? (
          /* Success — show the shareable event link */
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900">{isEdit ? 'Changes saved! 🎉' : 'Your event is live! 🎉'}</h4>
            <p className="mt-1 text-sm text-gray-500">Share this link — buyers can pay by card, no account needed.</p>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
              <span className="min-w-0 flex-1 truncate px-2 text-sm text-gray-700">{shareUrl}</span>
              <button onClick={copyLink} className="flex-shrink-0 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
            <div className="mt-4 flex gap-2">
              <a href={directUrl || shareUrl} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Preview</a>
              <button onClick={() => { reset(); }} className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Create another</button>
              <button onClick={() => { reset(); onClose(); }} className="flex-1 rounded-lg bg-teal-600 py-2.5 text-sm font-semibold text-white hover:bg-teal-700">Done</button>
            </div>
            <p className="mt-3 text-xs text-gray-400">Manage sales & see buyers in your dashboard's Tickets section.</p>
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Event title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Freshers Night 2026" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>

            {/* Where is the event? */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Where is the event?</label>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => setLocationType('school')}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${locationType === 'school' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-school-line mr-1"></i>Inside a school
                </button>
                <button type="button" onClick={() => setLocationType('outside')}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${locationType === 'outside' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-map-pin-line mr-1"></i>Outside school
                </button>
                <button type="button" onClick={() => setLocationType('online')}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${locationType === 'online' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-global-line mr-1"></i>Online
                </button>
              </div>
            </div>

            {locationType === 'online' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Event link (Zoom, Google Meet, YouTube, etc.)</label>
                <input value={onlineUrl} onChange={(e) => setOnlineUrl(e.target.value)} type="url" inputMode="url" placeholder="https://zoom.us/j/…" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                <p className="mt-0.5 text-[11px] text-gray-400">Buyers will see this link after a successful purchase.</p>
              </div>
            )}

            {/* Visibility: public (on marketplace) vs private (link-only) */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Who can find this event?</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setVisibility('public')}
                  className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${visibility === 'public' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-earth-line mr-1"></i>Public
                  <span className="mt-0.5 block text-[11px] font-normal text-gray-500">Shown on the Campket marketplace</span>
                </button>
                <button type="button" onClick={() => setVisibility('private')}
                  className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${visibility === 'private' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-lock-line mr-1"></i>Private
                  <span className="mt-0.5 block text-[11px] font-normal text-gray-500">Only people with your link can buy</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {locationType !== 'online' && (
              <div>
                {locationType === 'school' ? (
                  <>
                    <label className="mb-1 block text-sm font-medium text-gray-700">School</label>
                    <input list="ticket-schools" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="Start typing your school…" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    <datalist id="ticket-schools">
                      {NIGERIAN_UNIVERSITIES.map((u) => <option key={u} value={u} />)}
                    </datalist>
                  </>
                ) : (
                  <>
                    <label className="mb-1 block text-sm font-medium text-gray-700">State</label>
                    <select value={stateName} onChange={(e) => setStateName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      <option value="">Select state…</option>
                      {NIGERIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </>
                )}
              </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Event date</label>
                <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Venue</label>
              <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="e.g. Main Auditorium" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">About the event</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Line-up, dress code, what's included…" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>

            {/* Tiers */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Ticket types</label>
              <p className="mb-2 text-xs text-gray-400">Add as many as you need — e.g. Regular, VIP, Table.</p>
              <div className="space-y-2">
                {tiers.map((t, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 p-2.5">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <input value={t.name} onChange={(e) => patchTier(i, { name: e.target.value })} placeholder={i === 0 ? 'Name (e.g. Regular)' : 'Name (e.g. VIP)'} className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm" />
                      <div className="relative">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">₦</span>
                        <input type="number" value={t.price} onChange={(e) => patchTier(i, { price: e.target.value })} placeholder="Price" className="w-full rounded-lg border border-gray-300 py-1.5 pl-6 pr-2 text-sm" />
                      </div>
                      <input type="number" value={t.quantity} onChange={(e) => patchTier(i, { quantity: e.target.value })} placeholder="Quantity" className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm" />
                      <div className="sm:col-span-2">
                        <label className="mb-0.5 block text-[11px] font-medium text-gray-600">Benefits / what's included</label>
                        <textarea value={t.perks} onChange={(e) => patchTier(i, { perks: e.target.value })} rows={2} placeholder="e.g. Free drink, Front row, Backstage access" className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm" />
                      </div>
                      <div>
                        <input
                          value={t.codePrefix}
                          onChange={(e) => patchTier(i, { codePrefix: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6) })}
                          placeholder="Code prefix (e.g. R, V, VIP)"
                          className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                        />
                        <p className="mt-0.5 text-[11px] text-gray-400">
                          Codes look like <span className="font-mono">{(t.codePrefix || 'TL')}-XXXXX-1234</span>
                        </p>
                      </div>
                    </div>
                    {tiers.length > 1 && (
                      <button type="button" onClick={() => setTiers((prev) => prev.filter((_, idx) => idx !== i))} className="mt-1.5 text-xs text-red-500 hover:text-red-600">Remove this type</button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setTiers((prev) => [...prev, { name: '', price: '', quantity: '', perks: '', codePrefix: '' }])} className="mt-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200">+ Add ticket type</button>
            </div>

            {/* Flyer */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Event flyer / image</label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-4 text-sm text-gray-600 hover:border-teal-400">
                <i className="ri-image-add-line text-lg"></i> Upload flyer
                <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); if (e.target) e.target.value = ''; }} />
              </label>
              {(existingImages.length > 0 || previews.length > 0) && (
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {existingImages.map((url, i) => {
                    const isVid = /\.(mp4|webm|mov|m4v|mpeg|mpg|mkv)$/i.test(url);
                    return (
                      <div key={`ex-${i}`} className="relative h-20 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200">
                        {isVid ? <video src={url} muted className="h-full w-full object-cover" /> : <img src={url} alt="" className="h-full w-full object-cover" />}
                        <button type="button" onClick={() => setExistingImages((prev) => prev.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600"><i className="ri-close-line text-sm"></i></button>
                      </div>
                    );
                  })}
                  {previews.map((p, i) => (
                    <div key={`new-${i}`} className="relative h-20 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200">
                      {p.isVideo ? <video src={p.url} muted className="h-full w-full object-cover" /> : <img src={p.url} alt="" className="h-full w-full object-cover" />}
                      <button type="button" onClick={() => removeImage(i)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600"><i className="ri-close-line text-sm"></i></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          </div>
        )}

        {!shareUrl && (
          <div className="flex justify-end gap-2 border-t border-gray-200 p-4">
            <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="rounded-lg bg-purple-600 px-5 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60">
              {creating ? 'Saving…' : isEdit ? 'Save changes' : 'Create & get link'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTicketModal;
